import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH } from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { deriveSaveAsTemplateOutcome } from "./saveAsTemplateOutcome";
import { useQuoteDocumentTemplatesStore } from "./quoteDocumentTemplatesStore";

/** Field error shown when the name input is empty on submit. */
const NAME_REQUIRED_ERROR = "Enter a name for the template";

interface SaveAsTemplateSheetProps {
  /** Whether the sheet is open. */
  open: boolean;
  /** Opens and closes the sheet. */
  onOpenChange: (open: boolean) => void;
  /** The quote whose stored document is captured as the template. */
  quoteId: string;
}

/**
 * Slide-over for saving a quote's stored document as an org-wide template:
 * names the template and optionally makes it the organization default.
 * @param open - Whether the sheet is open
 * @param onOpenChange - Opens and closes the sheet
 * @param quoteId - The quote whose stored document is captured
 * @returns The save-as-template sheet element
 */
export function SaveAsTemplateSheet({
  open,
  onOpenChange,
  quoteId,
}: SaveAsTemplateSheetProps) {
  const createTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.createTemplate,
  );
  const updateQuoteSettings = useQuoteSettingsStore(
    (state) => state.updateSettings,
  );
  const [name, setName] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    setName("");
    setSetAsDefault(false);
    setNameError(null);
    setFormError(null);
    onOpenChange(false);
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(NAME_REQUIRED_ERROR);
      return;
    }
    setNameError(null);
    setFormError(null);
    setIsSaving(true);
    const result = await createTemplate({ quoteId, name: trimmed });
    const outcome = deriveSaveAsTemplateOutcome(result);

    if (outcome.kind === "name-taken") {
      setIsSaving(false);
      setNameError(outcome.message);
      return;
    }
    if (outcome.kind === "error") {
      setIsSaving(false);
      setFormError(outcome.message);
      return;
    }

    if (setAsDefault) {
      const updated = await updateQuoteSettings({
        defaultQuoteDocumentTemplateId: outcome.template.id,
      });
      if (!updated) {
        setIsSaving(false);
        setFormError(
          `"${outcome.template.name}" was saved, but could not be set as the organization default.`,
        );
        return;
      }
    }
    setIsSaving(false);
    toast.success(`"${outcome.template.name}" saved as a template.`);
    close();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
    >
      <SheetContent className="flex w-full flex-col sm:w-[520px] sm:max-w-[520px]">
        <SheetHeader>
          <SheetTitle>Save as template</SheetTitle>
          <SheetDescription>
            Saves this quote&apos;s document as a reusable template for your
            organization. Templates are fixed once saved, so saving always
            creates a new one.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto">
          <div className="space-y-2">
            <Label htmlFor="save-as-template-name">Template name</Label>
            <Input
              id="save-as-template-name"
              value={name}
              maxLength={QUOTE_DOCUMENT_TEMPLATE_NAME_MAX_LENGTH}
              placeholder="e.g. Standard proposal"
              autoFocus
              onChange={(event) => {
                setName(event.target.value);
                setNameError(null);
              }}
              aria-invalid={nameError !== null}
            />
            {nameError && (
              <p className="text-xs text-destructive">{nameError}</p>
            )}
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="save-as-template-default"
              checked={setAsDefault}
              onCheckedChange={(checked) => setSetAsDefault(checked === true)}
            />
            <div className="space-y-1">
              <Label
                htmlFor="save-as-template-default"
                className="font-normal leading-none"
              >
                Set as organization default
              </Label>
              <p className="text-xs text-muted-foreground">
                Applies to new quotes only; existing quotes are unchanged.
              </p>
            </div>
          </div>

          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </div>

        <SheetFooter className="flex-row justify-end gap-2 sm:space-x-0">
          <Button variant="outline" onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={save}>
            Save template
            {isSaving && <Loader2 className="size-4 animate-spin" />}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
